import { Form, Radio, RadioChangeEvent, Space } from 'antd'
import { t } from 'i18next'
import { FormInstance } from 'rc-field-form'
import { memo, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { SelectParticipant } from '../../../../components'
import { privacyTypes } from '../../../../constants/constants'
import { useDataSources, useDataStores } from '../../../../hooks'
import { RootState } from '../../../../store'
import { DataSource } from '../../../../store/reducers/dataSourceReducer'
import { DataStore } from '../../../../store/reducers/dataStoreReducer'
import { User } from '../../../../store/reducers/userReducer'

interface CreateWorkspaceModalPrivacyStepProps {
  form: FormInstance
  dataSource?: DataSource
  dataStore?: DataStore
}

const CreateWorkspaceModalPrivacyStep = memo(
  ({ form }: CreateWorkspaceModalPrivacyStepProps) => {
    const selectedDataSource = form.getFieldValue('selectedDataSource') as
      | DataSource
      | undefined
    const selectedDataStore = form.getFieldValue('selectedDataStore') as
      | DataStore
      | undefined

    const [isSelectDisabled, setIsSelectDisabled] = useState(
      form.getFieldValue('visibility') !== privacyTypes.PRIVATE
    )
    const currentUser = useSelector(
      (state: RootState) => state.user.currentUser
    )

    const [permittedUsers, setPermittedUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(false)

    const { getPermittedUsersOfDataSource } = useDataSources({
      preventFetch: true,
    })

    const { getPermittedUsersOfDataStore } = useDataStores({
      preventFetch: true,
    })

    useEffect(() => {
      const fetchPermittedUsers = async () => {
        if (!selectedDataSource?.id) {
          setPermittedUsers([])
          return
        }

        setLoading(true)
        try {
          const promises = [
            getPermittedUsersOfDataSource({
              dataSourceId: selectedDataSource.id,
            }),
            selectedDataStore?.id
              ? getPermittedUsersOfDataStore({
                  dataStoreId: selectedDataStore.id,
                })
              : Promise.resolve([]),
          ]

          const [users, dataStoreUsers] = await Promise.all(promises)
          const dataStoreUserIds = new Set(
            dataStoreUsers.map((user: User) => user.id)
          )
          // take set of users that are common to both.
          const commonUsers = users.filter((user: User) =>
            dataStoreUserIds.has(user.id)
          )
          setPermittedUsers(commonUsers)
        } catch (error) {
          console.error('Failed to fetch permitted users:', error)
        } finally {
          setLoading(false)
        }
      }

      fetchPermittedUsers()
    }, [
      selectedDataSource,
      selectedDataStore,
      getPermittedUsersOfDataSource,
      getPermittedUsersOfDataStore,
    ])

    const handleRadioChange = useCallback(
      async (e: RadioChangeEvent) => {
        const isPrivate = e.target.value === privacyTypes.PRIVATE
        const participants = isPrivate && currentUser ? [currentUser.id] : []
        setIsSelectDisabled(!isPrivate)
        form.setFieldsValue({ participants })
        if (isPrivate) {
          try {
            await form.validateFields(['participants'])
          } catch (error) {
            console.error('Failed to validate participants:', error)
          }
        }
      },
      [currentUser, form]
    )

    return (
      <>
        <Form.Item
          label={t('createWorkspaceModal.privacyStep.subHeader')}
          name="visibility"
          rules={[
            {
              required: true,
              message: t('errorMessage.isRequired') || '',
            },
          ]}
        >
          <Radio.Group buttonStyle="solid" onChange={handleRadioChange}>
            <Space direction="vertical">
              <Radio value={privacyTypes.PRIVATE}>
                {t('createWorkspaceModal.privacyStep.privateOption')}
              </Radio>
              <Radio
                value={privacyTypes.ORGANIZATION}
                disabled={
                  selectedDataSource?.visibility === privacyTypes.PRIVATE
                }
              >
                {t('createWorkspaceModal.privacyStep.organizationalOption')}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label={t('createWorkspaceModal.privacyStep.participantSelectHeader')}
          name="participants"
          rules={[
            {
              required: !isSelectDisabled,
              message: t('errorMessage.isRequired') || '',
            },
          ]}
          extra={
            t('createWorkspaceModal.privacyStep.extraInputPlaceholder') || ''
          }
        >
          <SelectParticipant
            disabled={isSelectDisabled}
            initialParticipants={form.getFieldValue('participants')}
            allParticipants={permittedUsers}
            loading={loading}
          />
        </Form.Item>
      </>
    )
  }
)

export default CreateWorkspaceModalPrivacyStep
